import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Activity, Eye, ShieldAlert, Trophy, TrendingDown,
  Wifi, Users as UsersIcon, AlertTriangle, Clock, Search,
} from "lucide-react";
import api from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Analytics() {
  const { startImpersonate } = useAuth();
  const [tab, setTab] = useState("command");
  const [live, setLive] = useState(null);
  const [loginStats, setLoginStats] = useState(null);
  const [productivity, setProductivity] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [access, setAccess] = useState({ logs: [], top_leads: [] });
  const [security, setSecurity] = useState({ failed_logins: [], locked_accounts: [], password_requests: [] });
  const [cmd, setCmd] = useState(null);

  const refresh = async () => {
    try {
      const [a, b, c, d, e, f, g] = await Promise.all([
        api.get("/analytics/command-center"),
        api.get("/analytics/live-users"),
        api.get("/analytics/login-stats"),
        api.get("/analytics/productivity"),
        api.get("/analytics/activity-timeline?limit=80"),
        api.get("/analytics/lead-access"),
        api.get("/analytics/security"),
      ]);
      setCmd(a.data); setLive(b.data); setLoginStats(c.data);
      setProductivity(d.data); setTimeline(e.data);
      setAccess(f.data); setSecurity(g.data);
    } catch (err) {
      toast.error("Failed to load analytics");
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="analytics-page">
      <header>
        <p className="text-label">Master Admin</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Live monitoring · productivity · security · audits</p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-secondary/60 flex-wrap h-auto">
          <TabsTrigger value="command" data-testid="atab-command">Command Center</TabsTrigger>
          <TabsTrigger value="live" data-testid="atab-live">Live Users</TabsTrigger>
          <TabsTrigger value="logins" data-testid="atab-logins">Logins</TabsTrigger>
          <TabsTrigger value="productivity" data-testid="atab-productivity">Productivity</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="atab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="access" data-testid="atab-access">Lead Access</TabsTrigger>
          <TabsTrigger value="security" data-testid="atab-security">Security</TabsTrigger>
        </TabsList>

        {/* COMMAND CENTER */}
        <TabsContent value="command" className="mt-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KPI label="Online Users" value={cmd?.online_users} icon={Wifi} accent="emerald" />
            <KPI label="Security Alerts" value={cmd?.security_alerts} icon={ShieldAlert} accent="red" />
            <KPI label="Failed Logins (today)" value={cmd?.failed_today} icon={AlertTriangle} accent="amber" />
            <KPI label="Stale Leads" value={cmd?.stale_leads} icon={Clock} accent="orange" />
            <KPI label="Follow-ups Due" value={cmd?.followups_due} icon={Activity} accent="blue" />
          </div>

          <Card title="Top Performers">
            {(cmd?.top_performers || []).length === 0 ? <Empty label="No performance data yet." /> :
              <div className="divide-y divide-border">
                {cmd.top_performers.map((p) => (
                  <div key={p.user_id} className="py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-sm flex items-center justify-center text-[11px] font-semibold text-white"
                        style={{ backgroundColor: "var(--theme-color)" }}>
                        {p.full_name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </div>
                      <div className="text-sm font-medium">{p.full_name}</div>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-4">
                      <span>{p.total} leads</span>
                      <span className="text-emerald-600 dark:text-emerald-400 mono font-medium">{p.won} won</span>
                    </div>
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>

        {/* LIVE USERS */}
        <TabsContent value="live" className="mt-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Online" value={live?.counts?.online} icon={Wifi} accent="emerald" />
            <KPI label="Active" value={live?.counts?.active} icon={Activity} accent="green" />
            <KPI label="Idle" value={live?.counts?.idle} icon={Clock} accent="amber" />
            <KPI label="Offline" value={live?.counts?.offline} icon={UsersIcon} accent="zinc" />
          </div>
          <Card title="User Sessions">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <Th>User</Th><Th>Role</Th><Th>Status</Th><Th>Page</Th><Th>Last Seen</Th><Th>IP</Th>
                </tr>
              </thead>
              <tbody>
                {[...(live?.online || []), ...(live?.idle || []), ...(live?.offline || [])].map((u) => (
                  <tr key={u.id} className="border-t border-border">
                    <Td className="font-medium">{u.full_name}</Td>
                    <Td className="text-xs">{ROLE_LABEL[u.role]}</Td>
                    <Td><StatusDot user={u} live={live} /></Td>
                    <Td className="text-xs mono">{u.page || "—"}</Td>
                    <Td className="text-xs text-muted-foreground mono">{u.last_seen ? new Date(u.last_seen).toLocaleString() : "Never"}</Td>
                    <Td className="text-xs mono">{u.ip || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* LOGINS */}
        <TabsContent value="logins" className="mt-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Today" value={loginStats?.today} icon={Activity} />
            <KPI label="This Week" value={loginStats?.week} icon={Activity} />
            <KPI label="This Month" value={loginStats?.month} icon={Activity} />
            <KPI label="Failed (today)" value={loginStats?.failed_today} icon={ShieldAlert} accent="red" />
            <KPI label="Locked Accounts" value={loginStats?.locked_accounts} icon={AlertTriangle} accent="amber" />
          </div>
        </TabsContent>

        {/* PRODUCTIVITY */}
        <TabsContent value="productivity" className="mt-5">
          <Card title="User Productivity">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr><Th>User</Th><Th>Role</Th><Th>Created</Th><Th>Assigned</Th>
                  <Th>Updated</Th><Th>Follow-ups</Th><Th>Won</Th><Th>Lost</Th><Th></Th></tr>
              </thead>
              <tbody>
                {productivity.map((p) => (
                  <tr key={p.user_id} className="border-t border-border">
                    <Td className="font-medium">{p.full_name}</Td>
                    <Td className="text-xs">{ROLE_LABEL[p.role]}</Td>
                    <Td className="mono">{p.leads_created}</Td>
                    <Td className="mono">{p.leads_assigned}</Td>
                    <Td className="mono">{p.leads_updated}</Td>
                    <Td className="mono">{p.follow_ups_done}</Td>
                    <Td className="mono text-emerald-600 dark:text-emerald-400">{p.won}</Td>
                    <Td className="mono text-red-600 dark:text-red-400">{p.lost}</Td>
                    <Td>
                      {p.role !== "master_admin" && (
                        <button
                          onClick={() => startImpersonate(p.user_id)}
                          data-testid={`impersonate-${p.user_id}`}
                          className="text-xs px-2 py-1 rounded-sm border border-border hover:bg-secondary text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" /> Login as
                        </button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* TIMELINE */}
        <TabsContent value="timeline" className="mt-5">
          <Card title="User Activity Timeline">
            <div className="divide-y divide-border">
              {timeline.length === 0 && <Empty label="No activity yet." />}
              {timeline.map((t) => (
                <div key={t.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`timeline-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium">{t.user_name || "—"}</span>
                      <span className="text-muted-foreground"> · {t.action.replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground text-xs"> · {t.resource_type}</span>
                    </div>
                    {t.meta && Object.keys(t.meta).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-0.5 mono truncate">
                        {JSON.stringify(t.meta)}
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mono whitespace-nowrap">
                    {new Date(t.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* LEAD ACCESS */}
        <TabsContent value="access" className="mt-5 space-y-4">
          <Card title="Top Viewed Leads">
            {access.top_leads.length === 0 ? <Empty label="No lead views yet." /> :
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr><Th>Lead</Th><Th>Views</Th><Th>Last Viewed</Th></tr>
                </thead>
                <tbody>
                  {access.top_leads.map((l) => (
                    <tr key={l._id} className="border-t border-border">
                      <Td className="mono text-xs">{l._id}</Td>
                      <Td className="mono">{l.views}</Td>
                      <Td className="text-xs text-muted-foreground mono">{new Date(l.last_viewed).toLocaleString()}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>}
          </Card>
          <Card title="Recent Lead Accesses">
            <div className="divide-y divide-border">
              {access.logs.slice(0, 30).map((l) => (
                <div key={l.id} className="py-2 flex items-center justify-between text-sm">
                  <span><span className="font-medium">{l.user_name}</span> viewed <span className="mono text-xs text-muted-foreground">{l.lead_id}</span></span>
                  <span className="text-xs text-muted-foreground mono">{new Date(l.opened_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* SECURITY */}
        <TabsContent value="security" className="mt-5 space-y-4">
          <Card title="Locked Accounts">
            {security.locked_accounts.length === 0 ? <Empty label="No locked accounts." /> :
              security.locked_accounts.map((u) => (
                <div key={u.id} className="py-2 text-sm flex items-center justify-between">
                  <span><span className="font-medium">{u.full_name}</span> · {ROLE_LABEL[u.role]}</span>
                  <span className="text-xs text-destructive">Locked</span>
                </div>
              ))}
          </Card>
          <Card title="Failed Logins (recent)">
            {security.failed_logins.length === 0 ? <Empty label="None." /> :
              <div className="divide-y divide-border max-h-72 overflow-auto">
                {security.failed_logins.map((f) => (
                  <div key={f.id} className="py-1.5 text-xs flex items-center justify-between">
                    <span className="mono">{f.identifier}</span>
                    <span className="text-muted-foreground mono">{f.ip || "—"} · {new Date(f.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>}
          </Card>
          <Card title="Password Reset Requests">
            {security.password_requests.length === 0 ? <Empty label="No pending requests." /> :
              <div className="divide-y divide-border">
                {security.password_requests.map((p) => (
                  <div key={p.id} className="py-2 text-sm flex items-center justify-between">
                    <span><span className="font-medium">{p.identifier}</span> {p.reason && <span className="text-muted-foreground"> · {p.reason}</span>}</span>
                    <span className="text-xs text-muted-foreground mono">{new Date(p.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ACCENTS = {
  emerald: "text-emerald-600 dark:text-emerald-400",
  green: "text-green-600 dark:text-green-400",
  red: "text-red-600 dark:text-red-400",
  amber: "text-amber-600 dark:text-amber-400",
  orange: "text-orange-600 dark:text-orange-400",
  blue: "text-blue-600 dark:text-blue-400",
  zinc: "text-muted-foreground",
};

function KPI({ label, value, icon: Icon, accent = "zinc" }) {
  return (
    <div className="surface border border-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-label">{label}</span>
        <Icon className={`w-4 h-4 ${ACCENTS[accent]}`} />
      </div>
      <div className="text-2xl font-semibold tracking-tight">{value ?? "—"}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="border border-border rounded-sm bg-background">
      <div className="px-5 py-3 border-b border-border"><h3 className="text-sm font-semibold">{title}</h3></div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Empty({ label }) { return <div className="text-sm text-muted-foreground text-center py-6">{label}</div>; }
function Th({ children }) { return <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-2 ${className}`}>{children}</td>; }
function StatusDot({ user, live }) {
  const isOnline = live?.online?.some((o) => o.id === user.id);
  const isIdle = live?.idle?.some((o) => o.id === user.id);
  if (isOnline) return <span className="inline-flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full bg-emerald-500" />Online</span>;
  if (isIdle) return <span className="inline-flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full bg-amber-500" />Idle</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><span className="w-2 h-2 rounded-full bg-zinc-400" />Offline</span>;
}
